<template>
  <b-form
      class="chat-app-form ta-chat-form"
      @submit.prevent="sendMessage"
  >
    <b-input-group class="input-group-merge form-send-message mr-1">
      <b-form-textarea
          ref="input"
          v-model="chatInputMessage"
          class="ta-chat-textarea"
          :placeholder="$t('chat.input.placeholder')"
          :disabled="!activeSession"
          rows="1"
          max-rows="6"
          no-resize
          @keydown.enter.exact.prevent="sendMessage"
          @input="onInput"
      />
    </b-input-group>
    <b-button
        variant="primary"
        type="submit"
        :disabled="!canSend"
    >
      {{ $t('chat.input.send') }}
    </b-button>
  </b-form>
</template>

<script>
import { BForm, BInputGroup, BFormTextarea, BButton } from 'bootstrap-vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'ChatInput',
  components: { BForm, BInputGroup, BFormTextarea, BButton },
  data() {
    return {
      chatInputMessage: '',
      lastTypedTime: null,
    }
  },
  computed: {
    ...mapGetters("chat", ["activeSession", "draftBySessionId"]),
    sessionId() {
      return this.activeSession?.id || null
    },
    canSend() {
      return this.sessionId != null && this.chatInputMessage != null && this.chatInputMessage.trim() !== ''
    },
  },
  watch: {
    // Only react to a real session switch — session object refreshes must never touch the text
    sessionId: {
      handler(newId, oldId) {
        if (oldId != null) {
          this.setDraft({ sessionId: oldId, text: this.chatInputMessage })
        }
        this.chatInputMessage = newId != null ? this.draftBySessionId(newId) : ''
        this.lastTypedTime = null
        this.focus()
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    if (this.sessionId != null) {
      this.setDraft({ sessionId: this.sessionId, text: this.chatInputMessage })
    }
  },
  methods: {
    ...mapActions("chat", ["sendChatMessage", "notifyTypingMessage"]),
    ...mapMutations("chat", ["setDraft"]),
    focus() {
      this.$nextTick(() => {
        const el = this.$refs.input?.$el
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    onInput() {
      if (this.sessionId != null) {
        this.setDraft({ sessionId: this.sessionId, text: this.chatInputMessage })
      }
      this.notifyTyping()
    },
    notifyTyping(){
      if (this.sessionId == null) return
      const currentTime = new Date().getTime();
      if (this.lastTypedTime && currentTime - this.lastTypedTime <= 2000) {
        return
      }
      this.lastTypedTime = currentTime
      this.notifyTypingMessage(this.sessionId)
          .catch((error) => {
            console.error('Error notifying typing:', error)
          })
    },
    sendMessage() {
      if (!this.canSend) return
      const sessionId = this.sessionId
      const chatInputMessage = this.chatInputMessage.trim()
      this.chatInputMessage = ''
      this.lastTypedTime = null
      this.setDraft({ sessionId, text: '' })
      const payload = {
        sessionId,
        messageRequest: { content: chatInputMessage },
      }
      this.$emit('message-sent', chatInputMessage)
      this.sendChatMessage(payload)
          .catch((error) => {
            console.error('Error sending message:', error)
            this.$emit('message-error', error)
          })
      this.focus()
    },
  }
}
</script>

<style lang="scss">
.chat-app-window .ta-chat-form {
  height: auto !important;
  min-height: 65px;
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
  align-items: flex-end !important;

  .ta-chat-textarea {
    overflow-y: auto;
    line-height: 1.45;
  }
}
</style>
